/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton, Tooltip } from '@mui/material';
import moment from 'moment';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AggregationInterval, ServiceAvailabilityValues } from '../../../shared/entities/dataUsage';
import { selectServiceAvailability } from '../../../store';
import { requestServiceAvailability } from '../../../store/dataUsage/actions';
import appTheme from '../../common/theme/appTheme.json';
import { PlaceholderHeading, PlaceholderText, PlaceholderWrapper } from '../DataUsageChartsCommonComponents';
import UsageBarChart from './UsageBarChart';
import DataUsageChartContainer, { ChartHeading } from './DataUsageChartContainer';

interface ServiceAvailabilityChartProps {
  width: number;
}

const ServiceAvailabilityChart = ({ width }: ServiceAvailabilityChartProps) => {
  const dispatch = useDispatch();
  //selectors
  const serviceAvailability = useSelector(selectServiceAvailability, shallowEqual);

  const series = [getSeriesData(serviceAvailability?.serviceAvailability ?? [])];
  return (
    <DataUsageChartContainer
      width={width}
      header={
        <Tooltip
          title="Service availability is calculated as the percentage of uptime in a given time period for selected terminals."
          placement="right-start"
          componentsProps={{
            tooltip: {
              sx: { backgroundColor: appTheme.secondaryBackgroundColor },
            },
          }}
        >
          <IconButton>
            <ChartHeading>Service Availability</ChartHeading>
            <div data-html2canvas-ignore="true">
              <InfoOutlinedIcon sx={{ color: appTheme.primaryTextColor, pl: 1 }} />
            </div>
          </IconButton>
        </Tooltip>
      }
      aggregationInterval={serviceAvailability?.aggregationInterval}
      menuItemClickHandler={(aggregationInterval: AggregationInterval) => {
        if (serviceAvailability) {
          dispatch(
            requestServiceAvailability({
              fromMsSinceEpoch: serviceAvailability?.fromMsSinceEpoch,
              toMsSinceEpoch: serviceAvailability?.toMsSinceEpoch,
              datapathUuids: serviceAvailability?.datapathUuids,
              aggregationInterval: aggregationInterval,
            }),
          );
        }
      }}
    >
      <UsageBarChart
        series={series}
        placeholder={
          <PlaceholderWrapper>
            <PlaceholderHeading>No Terminals</PlaceholderHeading>
            <PlaceholderText>To view data usage analytics, select one or multiple terminals</PlaceholderText>
          </PlaceholderWrapper>
        }
        showPlaceholder={serviceAvailability === undefined}
        aggregationInterval={serviceAvailability?.aggregationInterval}
        userEndDateMs={serviceAvailability?.toMsSinceEpoch}
        width={width}
      />
    </DataUsageChartContainer>
  );
};

export default ServiceAvailabilityChart;

export function getSeriesData(data: { [sourceTime: number]: ServiceAvailabilityValues } | undefined) {
  return {
    name: 'Service availability',
    tooltipShortenedName: 'Service availability',
    data: data ? translateChartData(data) : [],
  };
}

function translateChartData(data: { [sourceTime: number]: ServiceAvailabilityValues }) {
  return Object.keys(data)
    .sort()
    .map(timestamp => ({
      x: moment.unix(parseInt(timestamp)).toDate(),
      y: data[timestamp].serviceAvailabilityPercentage as number,
    }));
}
